import React, { Fragment, useState } from 'react';
import {
  EventNameSpan,
  Leg,
  LegSection,
  MarketNameContainer,
  SelectionNameContainer,
  struckThroughStyles
} from './legs-component';
import SGPLegsComponent from './sgp-legs-component';
import styled from 'styled-components';
import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import { formatHandicap, removePipes, withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import { DownChevron, UpChevron, SGP } from '@williamhill/rosi-ui-shared/src/assets/icons';
import { EventNamesContainer, LegsToggleContainer, SGPWrapper } from '.';
import {
  Block,
  DisplayHeading,
  Text,
  robotoFonts
} from '@williamhill/rosi-ui-shared/src/components/typography/Typography';
import { inferSGPResult } from '@williamhill/rosi-ui-shared/src/components/bet-details';
import { PriceComponent } from './price-component';
import { BetLeg } from '../../model';
import { ResultIcon } from './result-icon';

type SuperParlayLegsComponentProps = {
  legs: Leg[];
  betLegs: BetLeg[];
};

type SuperParlayLegProps = {
  legs: Leg[];
  betLeg: BetLeg;
};

function SuperParlayLeg({ legs, betLeg }: SuperParlayLegProps) {
  const [legsVisible, setLegsVisible] = useState(false);

  const superParlayLegs = (type: string) =>
    legs.filter(leg => {
      return betLeg.type === type && betLeg.legRefs.includes(leg.id);
    });

  const overallResult = inferSGPResult(legs, betLeg);

  return (
    <Fragment>
      <hr />
      {superParlayLegs('leg-group').length > 0 && (
        <BetSection>
          <ResultIcon resultType={overallResult} marginTop="0.6rem" />
          <BetSectionInner>
            <SGPHeader as="header">
              {superParlayLegs('leg-group').length} LEG SAME GAME PARLAY
              <SGPWrapper isSuperParlay>
                <SGP width="2rem" border={false} />
              </SGPWrapper>
              <PriceComponent
                price={betLeg.price}
                originalPrice={betLeg.originalPrice}
                isVoided={Boolean(overallResult && overallResult === 'void')}
              />
            </SGPHeader>
            <main>
              <BetSummary>
                <EventNamesContainer>
                  <EventNameSpan>
                    {dayjs(superParlayLegs('leg-group')[0].eventStartTime).format('MMM DD, h:mma')} |{' '}
                    {removePipes(superParlayLegs('leg-group')[0].event.name)}
                  </EventNameSpan>
                </EventNamesContainer>
                <LegsToggleContainer data-testid="sgp-leg-toggle" onClick={() => setLegsVisible(!legsVisible)}>
                  {legsVisible ? <UpChevron width="1.4rem" /> : <DownChevron width="1.4rem" />}
                </LegsToggleContainer>
              </BetSummary>
              {legsVisible && <SGPLegsComponent {...{ legs: superParlayLegs('leg-group') }} />}
            </main>
          </BetSectionInner>
        </BetSection>
      )}
      {superParlayLegs('standard').length > 0 && (
        <StraightLegs as="ul" weight="medium">
          {sortBy(superParlayLegs('standard'), ({ eventStartTime }) => new Date(eventStartTime).getTime()).map(
            ({ id, event, eventStartTime, market, selection, result, price, marketLine }) => {
              const withTestId = withRootTestId(`bet-tracker-bets-ticket-leg-${id}`);
              const selectionName = removePipes(selection.name);
              const isInjuryProtected = result?.type === 'injury_protection';
              const isVoid = result?.type === 'void';
              const isStruckThrough = isVoid || isInjuryProtected;

              return (
                <LegItem data-testid={withTestId()} key={id}>
                  <ResultIcon resultType={result?.type} />
                  <LegSection>
                    <header>
                      <SelectionNameContainer data-testid={withTestId('name')} isStruckThrough={isStruckThrough}>
                        {selectionName}
                        {marketLine && ` ${formatHandicap(marketLine, selectionName, false)}`}
                      </SelectionNameContainer>
                      <PriceComponent price={price} isVoided={isVoid || isInjuryProtected} />
                    </header>
                    <main>
                      <MarketNameContainer data-testid={withTestId('market')} isStruckThrough={isStruckThrough}>
                        {removePipes(market.name)}
                      </MarketNameContainer>
                      <EventDateAndName isStruckThrough={isStruckThrough}>
                        {dayjs(eventStartTime).format('MMM DD, h:mma')} |{' '}
                        <EventNameSpan data-testid={withTestId('event-name')}>{removePipes(event.name)}</EventNameSpan>
                      </EventDateAndName>
                    </main>
                  </LegSection>
                </LegItem>
              );
            }
          )}
        </StraightLegs>
      )}
    </Fragment>
  );
}

export default function SuperParlayLegsComponent({ legs, betLegs }: SuperParlayLegsComponentProps) {
  return (
    <>
      {betLegs.map((betLeg, i) => {
        return <SuperParlayLeg key={i} betLeg={betLeg} legs={legs} />;
      })}
    </>
  );
}

const StraightLegs = styled(Text)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const LegItem = styled.li`
  position: relative;
  display: flex;
  gap: 1rem;
`;

const BetSection = styled.section`
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  gap: 1rem;
  position: relative;

  & > header {
    font-weight: 700;
  }

  & hr {
    border-style: none;
    border-top: 1px solid ${({ theme }) => theme.colors.grey200};
    height: 1px;
    margin: 0.8rem 0;
  }
`;

const BetSectionInner = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`;

const BetSummary = styled.div`
  color: ${({ theme }) => theme.colors.brand.quaternary};
  display: flex;
  font-size: ${robotoFonts.sm};
  margin-top: 0.3rem;
`;

const SGPHeader = styled(DisplayHeading).attrs({ as: 'header', size: 'xs', weight: 'bold' })`
  display: flex;
  align-items: center;
`;

const EventDateAndName = styled(Block).attrs({ weight: 'regular', size: 'sm' })<{ isStruckThrough?: boolean }>`
  ${({ isStruckThrough }) => isStruckThrough && struckThroughStyles}
`;
